// Compact multi-select for pipeline stages. Click to open a checkbox panel.
// Used by the Deal & Lead pipelines (table view) to filter by one or more stages.
import { useEffect, useRef, useState } from "react";
import { Layers, Check, ChevronDown } from "lucide-react";

export default function PipelineStageFilter({ stages = [], selected = [], onChange, facets = {}, testidPrefix = "stage" }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const toggle = (key) => {
    const set = new Set(selected);
    set.has(key) ? set.delete(key) : set.add(key);
    onChange(Array.from(set));
  };

  const count = selected.length;
  const label = count === 0 ? "Stage: any" : count === 1
    ? (stages.find((s) => s.key === selected[0])?.label || "1 stage")
    : `${count} stages`;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`${testidPrefix}-filter-btn`}
        className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border text-[12px] outline-none transition-colors ${
          count ? "filter-active border-orange-400/60 text-orange-100 bg-orange-500/10" : "border-white/10 text-slate-300 hover:text-white hover:border-white/25"
        }`}
        title="Filter by pipeline stage"
      >
        <Layers className="w-3.5 h-3.5" />
        <span className="max-w-[160px] truncate">{label}</span>
        <ChevronDown className="w-3 h-3 opacity-60" />
      </button>

      {open && (
        <div
          data-testid={`${testidPrefix}-filter-panel`}
          className="absolute z-50 mt-1 w-[280px] max-h-[360px] overflow-y-auto rounded-lg border border-white/15 bg-[#11161f] shadow-2xl p-1.5"
        >
          <div className="flex items-center justify-between px-2 py-1.5">
            <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Stages</span>
            {count > 0 && (
              <button
                type="button"
                onClick={() => onChange([])}
                data-testid={`${testidPrefix}-filter-clear`}
                className="text-[10px] font-mono uppercase tracking-wider text-rose-300 hover:text-rose-200"
              >
                Clear
              </button>
            )}
          </div>
          {stages.map((s) => {
            const on = selected.includes(s.key);
            const n = facets?.[s.key];
            return (
              <button
                key={s.key}
                type="button"
                onClick={() => toggle(s.key)}
                data-testid={`${testidPrefix}-opt-${s.key}`}
                className={`w-full flex items-center gap-2.5 px-2 py-1.5 rounded-md text-left text-[12.5px] transition-colors ${
                  on ? "bg-orange-500/15 text-orange-100" : "text-slate-200 hover:bg-white/[0.05]"
                }`}
              >
                <span className={`w-4 h-4 rounded border grid place-items-center shrink-0 ${on ? "bg-orange-400 border-orange-400" : "border-white/25"}`}>
                  {on && <Check className="w-3 h-3 text-black" />}
                </span>
                <span className="flex-1 truncate">{s.label}</span>
                {n != null && <span className="text-[10px] font-mono text-slate-500">{n}</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
